import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { getClients, getProject, getProjects, saveClient, saveProject } from '@/services/clientService'
import { normalizeEntityId, toShortEntityId } from '@/lib/entityUrl'
import type { Client, Project } from '@/types'

export interface ClientProjectContextType {
  clientId: string | undefined
  projectId: string | undefined
  setClientId: (id: string | undefined) => void
  setProjectId: (id: string | undefined) => void
  clients: Client[]
  allProjects: (Project & { clientUuid: string })[]
  projects: (Project & { clientUuid: string })[]
  loadingClients: boolean
  loadingProjects: boolean
  /** Ensure a given client's projects are loaded into allProjects (cached; hits getProjects at most once per client). */
  loadProjectsForClient: (clientId: string | undefined) => Promise<void>
  selectedClient: Client | undefined
  selectedProject: (Project & { clientUuid: string }) | undefined
  createClient: (name: string, address?: string, otherNames?: string[]) => Promise<Client>
  createProject: (name: string, clientUuid: string) => Promise<Project>
  /**
   * Scope derived from an open document (e.g. the analysis page's contract → its project),
   * filling the dropdowns WITHOUT writing anything to the URL. On document pages the document
   * is the URL's only identity; its ancestors are derived state. An explicit selection via
   * setClientId/setProjectId (which writes the URL) always wins and clears this.
   */
  setDerivedScope: (scope: { clientId?: string; projectId?: string }) => void
}

const ClientProjectContext = createContext<ClientProjectContextType | null>(null)

export function ClientProjectProvider({ children }: { children: ReactNode }) {
  // The URL is the single source of truth for the EXPLICIT selection, and it holds only the
  // lowest-level entity: ?projectId when a project is selected (its client is implied and
  // derived), or ?clientId when only a client is. Both id forms are accepted (short base62 is
  // canonical; dashed UUIDs survive from old links). Document pages carry neither — they
  // register their document's project as DERIVED scope below.
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const routerState = useRouterState()
  const search = routerState.location.search as Record<string, string | undefined>
  const urlClientId = normalizeEntityId(search.clientId)
  const urlProjectId = normalizeEntityId(search.projectId)

  // Scope derived from an open document (analysis page). Never written to the URL; explicit
  // selection clears it so the URL is the sole driver again.
  const [derivedScope, setDerivedScopeState] = useState<{ clientId?: string; projectId?: string }>({})
  const setDerivedScope = useCallback((scope: { clientId?: string; projectId?: string }) => {
    setDerivedScopeState((prev) =>
      prev.clientId === scope.clientId && prev.projectId === scope.projectId ? prev : scope,
    )
  }, [])

  const projectId = urlProjectId ?? derivedScope.projectId

  const [clients, setClients] = useState<Client[]>([])
  const [allProjects, setAllProjects] = useState<(Project & { clientUuid: string })[]>([])
  const [loadingClients, setLoadingClients] = useState(true)
  // Start "loading" only if we already have a selection to load projects for (e.g. a deep link
  // with ?clientId= or ?projectId=), so the dropdown shows a spinner rather than a flash of
  // "No projects found".
  const [loadingProjects, setLoadingProjects] = useState(() => Boolean(urlClientId || urlProjectId))

  // The selected project's client is derived, not carried in the URL. Until the project is
  // resolvable (deep links can name a project we haven't loaded — see the effect below), fall
  // back to an explicit URL client (legacy links carry both) or the derived scope's client.
  const clientId = projectId
    ? allProjects.find((p) => p.uuid === projectId)?.clientUuid ?? urlClientId ?? derivedScope.clientId
    : urlClientId ?? derivedScope.clientId
  const clientIdRef = useRef(clientId)
  clientIdRef.current = clientId

  // Load the client list on mount. Projects are loaded lazily, per client (below), so we never fan a
  // getProjects request across every client in the system just to populate the project dropdown.
  useEffect(() => {
    const abortController = new AbortController()
    const aborted = () => abortController.signal.aborted

    async function loadClientList() {
      try {
        const clientsData = await getClients()
        if (aborted()) return
        setClients(clientsData)
      } catch (error) {
        console.error('Failed to load clients:', error)
      } finally {
        if (!aborted()) setLoadingClients(false)
      }
    }

    loadClientList()
    return () => { abortController.abort() }
  }, [])

  // Per-client project cache. getProjects is hit at most once per client, and only for clients the
  // user actually opens — never eagerly for all of them. Results merge into allProjects so previously
  // visited clients (and other-client lookups, e.g. the user-management page) stay resolvable.
  const loadedClientsRef = useRef<Set<string>>(new Set())
  const loadProjectsForClient = useCallback(async (cid: string | undefined) => {
    if (!cid || loadedClientsRef.current.has(cid)) {
      setLoadingProjects(false)
      return
    }
    loadedClientsRef.current.add(cid)
    setLoadingProjects(true)
    try {
      const fetched = await getProjects(cid)
      const withClient = fetched.map((p) => ({ ...p, clientUuid: cid }))
      setAllProjects((prev) => {
        const byUuid = new Map(prev.map((p) => [p.uuid, p]))
        for (const p of withClient) byUuid.set(p.uuid, p)
        return Array.from(byUuid.values())
      })
    } catch (error) {
      console.error(`Failed to load projects for client ${cid}:`, error)
      loadedClientsRef.current.delete(cid) // allow a later retry
    } finally {
      setLoadingProjects(false)
    }
  }, [])

  // Load the currently-selected client's projects (drives the dropdown and everything keyed off
  // allProjects). Re-runs on clientId change; cached clients short-circuit without a refetch.
  useEffect(() => {
    loadProjectsForClient(clientId)
  }, [clientId, loadProjectsForClient])

  // A deep link (or derived scope) can name a project that isn't in allProjects yet — the URL no
  // longer carries its client, so resolve the project directly to learn it. Once it lands, the
  // clientId derivation above picks up its clientUuid and the client's full project list loads
  // via the effect above. The ref stops a re-fetch loop while a failed id stays in the URL.
  const resolvingProjectsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!projectId) return
    if (allProjects.some((p) => p.uuid === projectId)) return
    if (resolvingProjectsRef.current.has(projectId)) return
    resolvingProjectsRef.current.add(projectId)
    getProject(projectId)
      .then((project) => {
        setAllProjects((prev) => (prev.some((p) => p.uuid === project.uuid) ? prev : [...prev, project]))
      })
      .catch((error) => {
        console.error(`Failed to resolve project ${projectId}:`, error)
      })
  }, [projectId, allProjects])

  // Stable ref for allProjects so callbacks don't need it as a dependency
  const allProjectsRef = useRef(allProjects)
  allProjectsRef.current = allProjects

  // Explicit client selection. The URL gets the minimal identity: if the current project already
  // belongs to the chosen client, the project stays the (sufficient) identity and no clientId is
  // written; otherwise the project clears and the client becomes the identity.
  const handleSetClientId = useCallback((id: string | undefined) => {
    setDerivedScopeState({})
    navigateRef.current({
      search: ((prev: Record<string, unknown>) => {
        const prevProjectId = normalizeEntityId(prev.projectId as string | undefined)
        const keepProject = Boolean(
          id && prevProjectId &&
          allProjectsRef.current.find((p) => p.uuid === prevProjectId)?.clientUuid === id,
        )
        return {
          ...prev,
          clientId: !id || keepProject ? undefined : toShortEntityId(id),
          projectId: keepProject ? prev.projectId : undefined,
        }
      }) as any,
      replace: true,
    })
  }, [])

  // Explicit project selection. A project is a sufficient identity on its own (its client is
  // derived), so selecting one removes any clientId from the URL; clearing the project restores
  // the current client as the identity so the selection doesn't vanish entirely.
  const handleSetProjectId = useCallback((id: string | undefined) => {
    setDerivedScopeState({})
    navigateRef.current({
      search: ((prev: Record<string, unknown>) => {
        if (id) {
          return { ...prev, projectId: toShortEntityId(id), clientId: undefined }
        }
        const fallbackClient = clientIdRef.current
        return {
          ...prev,
          projectId: undefined,
          clientId: fallbackClient ? toShortEntityId(fallbackClient) : undefined,
        }
      }) as any,
      replace: true,
    })
  }, [])

  const projects = useMemo(() =>
    clientId ? allProjects.filter((p) => p.clientUuid === clientId) : allProjects,
    [clientId, allProjects]
  )

  const selectedClient = useMemo(() => clients.find((c) => c.uuid === clientId), [clients, clientId])
  const selectedProject = useMemo(() => allProjects.find((p) => p.uuid === projectId), [allProjects, projectId])

  const createClient = useCallback(async (name: string, address?: string, otherNames?: string[]) => {
    const newClient = await saveClient({ name, address, otherNames })
    setClients((prev) => [...prev, newClient])
    return newClient
  }, [])

  const createProject = useCallback(async (name: string, clientUuid: string) => {
    const newProject = await saveProject(clientUuid, { name, clientUuid })
    const projectWithClient = { ...newProject, clientUuid }
    setAllProjects((prev) => [...prev, projectWithClient])
    return newProject
  }, [])

  const value = useMemo<ClientProjectContextType>(() => ({
    clientId,
    projectId,
    setClientId: handleSetClientId,
    setProjectId: handleSetProjectId,
    clients,
    allProjects,
    projects,
    loadingClients,
    loadingProjects,
    loadProjectsForClient,
    selectedClient,
    selectedProject,
    createClient,
    createProject,
    setDerivedScope,
  }), [
    clientId, projectId, handleSetClientId, handleSetProjectId,
    clients, allProjects, projects,
    loadingClients, loadingProjects, loadProjectsForClient,
    selectedClient, selectedProject,
    createClient, createProject, setDerivedScope,
  ])

  return (
    <ClientProjectContext.Provider value={value}>
      {children}
    </ClientProjectContext.Provider>
  )
}

export function useClientProject() {
  const context = useContext(ClientProjectContext)
  if (!context) {
    throw new Error('useClientProject must be used within ClientProjectProvider')
  }
  return context
}
